import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Row,
  Spinner,
  Progress,
  Input,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from "reactstrap";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useLocation, useNavigate, useBlocker } from 'react-router-dom';
import { getSoalTryout, tryoutAddResultsDetails } from '../../../services/tryoutService';

const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes < 10 ? '0' : ''}${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
};

const TryoutExam = () => {
  document.title = "Kerjakan Tryout";

  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const idPaket = queryParams.get('id_paket');
  const resultId = queryParams.get('result_id');
  const idJenisSeleksi = queryParams.get('id_jenis_seleksi');
  const idTipeKonten = queryParams.get('id_tipe_konten'); // <--- BARU: Ambil id_tipe_konten dari URL

  const [examData, setExamData] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBlockerModalOpen, setIsBlockerModalOpen] = useState(false);
  
  const timerRef = useRef(null);
  const questionStartTimeRef = useRef(null);
  const userAnswersRef = useRef({});
  const lastActiveTimeRef = useRef(Date.now());

  // Key untuk localStorage
  const TIMER_STORAGE_KEY = `tryout_timer_${resultId}`;
  const ANSWERS_STORAGE_KEY = `tryout_answers_${resultId}`;
  const CURRENT_Q_INDEX_STORAGE_KEY = `tryout_current_q_index_${resultId}`;
  const QUESTION_START_TIME_STORAGE_KEY = `tryout_question_start_time_${resultId}`;
  const LAST_ACTIVE_TIME_STORAGE_KEY = `tryout_last_active_time_${resultId}`;

  // Fungsi untuk toggle modal konfirmasi
  const toggleConfirmModal = useCallback(() => {
    setIsConfirmModalOpen(prev => !prev);
  }, []);

  // Fungsi untuk menangani perubahan jawaban
  const handleAnswerChange = (questionId, selectedOptionKey) => {
    setUserAnswers(prevAnswers => ({
      ...prevAnswers,
      [questionId]: {
        ...prevAnswers[questionId],
        answer: selectedOptionKey
      }
    }));
  };

  // Fungsi untuk navigasi soal berikutnya
  const handleNextQuestion = useCallback(() => {
    if (examData?.daftar_soal && currentQuestionIndex < examData.daftar_soal.length - 1) {
      recordTimeSpent(examData.daftar_soal[currentQuestionIndex].id_soal);
      setCurrentQuestionIndex(prevIndex => prevIndex + 1);
    }
  }, [currentQuestionIndex, examData]);

  // Fungsi untuk navigasi soal sebelumnya
  const handlePreviousQuestion = useCallback(() => {
    if (examData?.daftar_soal && currentQuestionIndex > 0) {
      recordTimeSpent(examData.daftar_soal[currentQuestionIndex].id_soal);
      setCurrentQuestionIndex(prevIndex => prevIndex - 1);
    }
  }, [currentQuestionIndex, examData]);

  // Fungsi untuk navigasi langsung ke soal tertentu
  const handleQuestionNavigation = useCallback((index) => {
    if (index !== currentQuestionIndex && examData?.daftar_soal) {
      recordTimeSpent(examData.daftar_soal[currentQuestionIndex].id_soal);
      setCurrentQuestionIndex(index);
    }
  }, [currentQuestionIndex, examData]);

  // Fungsi untuk mencatat waktu yang dihabiskan di sebuah soal
  const recordTimeSpent = useCallback((questionId, isFinal = false) => {
    if (questionStartTimeRef.current && questionId) {
      const timeSpent = Math.floor((Date.now() - questionStartTimeRef.current) / 1000);

      setUserAnswers(prevAnswers => {
        const prevData = prevAnswers[questionId] || { answer: null, timeSpent: 0 };
        const totalTimeSpent = prevData.timeSpent + timeSpent;

        const updatedAnswers = {
          ...prevAnswers,
          [questionId]: {
            ...prevData,
            timeSpent: totalTimeSpent,
          }
        };
        userAnswersRef.current = updatedAnswers;
        return updatedAnswers;
      });

      if (!isFinal) {
        questionStartTimeRef.current = Date.now();
        localStorage.setItem(QUESTION_START_TIME_STORAGE_KEY, Date.now().toString());
      }
    }
  }, []);

  // Fungsi untuk menangani perubahan visibility tab/window
  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'visible') {
      const now = Date.now();
      const lastActive = parseInt(localStorage.getItem(LAST_ACTIVE_TIME_STORAGE_KEY)) || now;
      const elapsedSeconds = Math.floor((now - lastActive) / 1000);
      
      if (elapsedSeconds > 0) {
        setTimeLeft(prev => {
          const newTime = Math.max(0, prev - elapsedSeconds);
          localStorage.setItem(TIMER_STORAGE_KEY, newTime.toString());
          return newTime;
        });
      }
      
      localStorage.setItem(LAST_ACTIVE_TIME_STORAGE_KEY, now.toString());
      lastActiveTimeRef.current = now;
    }
  }, []);

  // Fungsi untuk menyelesaikan ujian
  const handleFinishExam = useCallback(async () => {
    setIsConfirmModalOpen(false);

    if (isSubmitting) return;

    setIsSubmitting(true);
    clearInterval(timerRef.current);

    if (examData?.daftar_soal && examData.daftar_soal[currentQuestionIndex]) {
      recordTimeSpent(examData.daftar_soal[currentQuestionIndex].id_soal, true);
    }

    await new Promise(resolve => setTimeout(resolve, 10));

    const finalUserAnswers = userAnswersRef.current;

    const payload = {
      id_tryout_answer: resultId,
      id_paket: idPaket,
      menit: Math.floor(timeLeft / 60),
      detik: timeLeft % 60,
      jawaban: Object.keys(finalUserAnswers).map(questionId => {
        const rawAnswerData = finalUserAnswers[questionId];
        let formattedAnswer;

        if (rawAnswerData && typeof rawAnswerData.answer === 'string' && rawAnswerData.answer.startsWith('opsi_')) {
          formattedAnswer = rawAnswerData.answer.slice(-1).toUpperCase();
        } else {
          formattedAnswer = "N";
        }

        return {
          id_soal: parseInt(questionId),
          jawaban: formattedAnswer,
          durasi: rawAnswerData.timeSpent || 0
        };
      })
    };

    try {
      const response = await tryoutAddResultsDetails(payload);
      console.log("Submission successful:", response);

   toast.success("Ujian selesai! Jawaban Anda telah disimpan.", {
  onClose: () => {
    // Bersihkan localStorage
    [
      TIMER_STORAGE_KEY,
      ANSWERS_STORAGE_KEY,
      CURRENT_Q_INDEX_STORAGE_KEY,
      QUESTION_START_TIME_STORAGE_KEY,
      LAST_ACTIVE_TIME_STORAGE_KEY,
    ].forEach((key) => {
      localStorage.removeItem(key);
    });

    setIsBlockerModalOpen(false);
    setIsConfirmModalOpen(false);
    
    // Gunakan navigate(-1) untuk kembali ke halaman sebelumnya
    navigate(-1);
  },
  autoClose: 3000,
});

    } catch (submitError) {
      console.error("Error submitting exam:", submitError);
      toast.error(`Gagal menyimpan jawaban ujian: ${submitError.message || 'Terjadi kesalahan'}`, {
        autoClose: 3000
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [timeLeft, idPaket, resultId, currentQuestionIndex, examData, isSubmitting, navigate]);

  // Fungsi untuk memuat data ujian
  const fetchExamData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      if (!idPaket || !resultId || !idJenisSeleksi || !idTipeKonten) { // <--- Cek idTipeKonten
        throw new Error("Parameter 'result_id', 'id_paket', 'id_jenis_seleksi', atau 'id_tipe_konten' tidak ditemukan di URL.");
      }

      // Panggilan API diubah: Mengirim idTipeKonten
      const response = await getSoalTryout(idPaket, idJenisSeleksi, idTipeKonten);

      if (response?.success && response.data) {
        if (!response.data.daftar_soal || !Array.isArray(response.data.daftar_soal)) {
          throw new Error("Data soal tidak valid atau kosong.");
        }

        setExamData(response.data);

        // Inisialisasi timer dengan memperhitungkan waktu yang terlewat
        const initialDuration = response.data.durasi_menit * 60;
        const savedTime = localStorage.getItem(TIMER_STORAGE_KEY);
        const lastActiveTime = localStorage.getItem(LAST_ACTIVE_TIME_STORAGE_KEY);

        let calculatedTimeLeft = initialDuration;

        if (savedTime && parseInt(savedTime) > 0 && parseInt(savedTime) <= initialDuration) {
          calculatedTimeLeft = parseInt(savedTime);

          if (lastActiveTime) {
            const elapsedTime = Math.floor((Date.now() - parseInt(lastActiveTime)) / 1000);
            calculatedTimeLeft = Math.max(0, calculatedTimeLeft - elapsedTime);
          }
        }

        setTimeLeft(calculatedTimeLeft);
        localStorage.setItem(LAST_ACTIVE_TIME_STORAGE_KEY, Date.now().toString());
        lastActiveTimeRef.current = Date.now();

        // Inisialisasi jawaban user
        const initialAnswers = {};
        response.data.daftar_soal.forEach(q => {
          initialAnswers[q.id_soal] = { answer: null, timeSpent: 0 };
        });

        const savedAnswers = localStorage.getItem(ANSWERS_STORAGE_KEY);
        if (savedAnswers) {
          try {
            const parsedAnswers = JSON.parse(savedAnswers);
            setUserAnswers({ ...initialAnswers, ...parsedAnswers });
            userAnswersRef.current = { ...initialAnswers, ...parsedAnswers };
          } catch (e) {
            console.error("Gagal mengurai jawaban dari localStorage:", e);
            setUserAnswers(initialAnswers);
            userAnswersRef.current = initialAnswers;
          }
        } else {
          setUserAnswers(initialAnswers);
          userAnswersRef.current = initialAnswers;
        }

        // Inisialisasi indeks soal saat ini
        const savedIndex = localStorage.getItem(CURRENT_Q_INDEX_STORAGE_KEY);
        const initialQuestionIndex = savedIndex ? 
          Math.min(parseInt(savedIndex), response.data.daftar_soal.length - 1) : 0;
        setCurrentQuestionIndex(initialQuestionIndex);

        // Inisialisasi waktu mulai soal
        const savedQuestionStartTime = localStorage.getItem(QUESTION_START_TIME_STORAGE_KEY);
        questionStartTimeRef.current = savedQuestionStartTime ? 
          parseInt(savedQuestionStartTime) : Date.now();
      } else {
        throw new Error(response?.message || "Gagal memuat data tryout: Respons tidak valid.");
      }
    } catch (err) {
      console.error("Failed to fetch tryout data:", err);
      setError(err.message || "Terjadi kesalahan saat memuat tryout.");
      toast.error(err.message || "Gagal memuat data tryout.");
    } finally {
      setIsLoading(false);
    }
  }, [idPaket, resultId, idJenisSeleksi, idTipeKonten]); // <--- Tambahkan idTipeKonten ke dependensi

  // Setup blocker untuk navigasi
  const shouldBlockNavigation = examData && timeLeft > 0 && !isSubmitting;
  const blocker = useBlocker(shouldBlockNavigation);
  const { proceed, reset, state } = blocker;

  useEffect(() => {
    if (state === 'blocked') {
      setIsBlockerModalOpen(true);
    } else if (state === 'unblocked' && isBlockerModalOpen) {
      setIsBlockerModalOpen(false);
    }
  }, [state, isBlockerModalOpen]);

  const confirmBlockerNavigation = useCallback(async () => {
    setIsBlockerModalOpen(false);
    await handleFinishExam();
    if (proceed) proceed();
  }, [handleFinishExam, proceed]);

  const cancelBlockerNavigation = useCallback(() => {
    setIsBlockerModalOpen(false);
    if (reset) reset();
  }, [reset]);

  // Efek untuk setup timer
  useEffect(() => {
    if (timeLeft > 0 && !isLoading && examData && !isSubmitting) {
      clearInterval(timerRef.current);

      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          const newTime = prev - 1;
          localStorage.setItem(TIMER_STORAGE_KEY, newTime.toString());
          localStorage.setItem(LAST_ACTIVE_TIME_STORAGE_KEY, Date.now().toString());
          lastActiveTimeRef.current = Date.now();

          if (newTime <= 0) {
            clearInterval(timerRef.current);
            handleFinishExam();
            toast.info("Waktu habis! Ujian otomatis diselesaikan.");
          }
          return newTime;
        });
      }, 1000);
    }

    return () => {
      clearInterval(timerRef.current);
    };
  }, [timeLeft, isLoading, examData, isSubmitting, handleFinishExam]);

  // Efek untuk setup visibility change listener
  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [handleVisibilityChange]);

  // Efek untuk inisialisasi komponen
  useEffect(() => {
    fetchExamData();
    
    return () => {
      clearInterval(timerRef.current);
      localStorage.setItem(LAST_ACTIVE_TIME_STORAGE_KEY, Date.now().toString());
      localStorage.setItem(ANSWERS_STORAGE_KEY, JSON.stringify(userAnswersRef.current));
      localStorage.setItem(CURRENT_Q_INDEX_STORAGE_KEY, currentQuestionIndex.toString());
      if (questionStartTimeRef.current) {
        localStorage.setItem(QUESTION_START_TIME_STORAGE_KEY, questionStartTimeRef.current.toString());
      }
    };
  }, [fetchExamData]);

  // Efek untuk menyimpan jawaban ke localStorage
  useEffect(() => {
    userAnswersRef.current = userAnswers;
    if (examData && Object.keys(userAnswers).length > 0) {
      localStorage.setItem(ANSWERS_STORAGE_KEY, JSON.stringify(userAnswers));
    }
  }, [userAnswers]);

  // Efek untuk menyimpan indeks soal saat ini
  useEffect(() => {
    if (examData) {
      localStorage.setItem(CURRENT_Q_INDEX_STORAGE_KEY, currentQuestionIndex.toString());
      if (!questionStartTimeRef.current) {
        questionStartTimeRef.current = Date.now();
        localStorage.setItem(QUESTION_START_TIME_STORAGE_KEY, Date.now().toString());
      }
    }
  }, [currentQuestionIndex]);

  // Render loading state
  if (isLoading) {
    return (
      <div className="page-content d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '80vh', backgroundColor: '#f5f7fa' }}>
        <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
        <p className="mt-2 text-primary fw-bold">Memuat soal tryout, mohon tunggu...</p>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="page-content d-flex flex-column justify-content-center align-items-center bg-light" style={{ minHeight: '80vh' }}>
        <i className="bx bx-error text-danger display-4 mb-3"></i>
        <p className="text-danger font-size-18 text-center">{error}</p>
        <Button color="primary" className="mt-3" onClick={() => navigate('/dashboard')}>Kembali ke Dashboard</Button>
      </div>
    );
  }

  // Render jika data soal tidak valid
  if (!examData || !examData.daftar_soal || examData.daftar_soal.length === 0) {
    return (
      <div className="page-content d-flex flex-column justify-content-center align-items-center bg-light" style={{ minHeight: '80vh' }}>
        <i className="bx bx-file-blank text-info display-4 mb-3"></i>
        <p className="font-size-18 text-center">
          {examData ? "Tidak ada soal untuk tryout ini." : "Data tryout tidak ditemukan atau tidak valid."}
        </p>
        <Button color="primary" className="mt-3" onClick={() => navigate('/paket-simulasi?kategori_paket=TRYOUT')}>
          Kembali ke Paket Simulasi
        </Button>
      </div>
    );
  }

  const currentQuestion = examData.daftar_soal[currentQuestionIndex];
  if (!currentQuestion) {
    return (
      <div className="page-content d-flex flex-column justify-content-center align-items-center bg-light" style={{ minHeight: '80vh' }}>
        <i className="bx bx-error text-danger display-4 mb-3"></i>
        <p className="font-size-18 text-center">Soal tidak ditemukan pada indeks saat ini.</p>
        <Button color="primary" className="mt-3" onClick={() => navigate('/paket-simulasi?kategori_paket=TRYOUT')}>
          Kembali ke Paket Simulasi
        </Button>
      </div>
    );
  }

  const totalQuestions = examData.daftar_soal.length;
  const answeredQuestionsCount = Object.values(userAnswers).filter(
    answerData => answerData.answer !== null && answerData.answer !== undefined
  ).length;
  const progressPercentage = (answeredQuestionsCount / totalQuestions) * 100;

  const options = ['a', 'b', 'c', 'd', 'e'];
  const questionOptions = options.map(optChar => ({
    key: `opsi_${optChar}`,
    char: optChar.toUpperCase(),
    text: currentQuestion[`opsi_${optChar}`],
    image: currentQuestion[`image_opsi_${optChar}`]
  })).filter(opt => opt.text !== undefined && opt.text !== null && opt.text !== '');

  return (
    <React.Fragment>
      <div className="page-content" style={{ backgroundColor: '#f8f9fa' }}>
        <Container fluid>
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
            <h5 className="mb-0 fw-bold">KERJAKAN TRYOUT CPNS FREE 1 (KISI-KISI 2025)</h5>
            <div className="d-flex align-items-center">
              <span className="text-muted me-2">Tryout / Kerjakan</span>
              <Button color="light" className="text-muted px-2 py-1">LAPORKAN SOAL</Button>
            </div>
          </div>

          <Row className="g-3">
            {/* Kolom Soal */}
            <Col lg={9}>
              <Card className="border-0 shadow-sm">
                <CardBody className="p-3">
                  <div className="d-flex justify-content-between align-items-center pb-2 mb-3 border-bottom">
                    <CardTitle className="h5 mb-0 d-flex align-items-center">
                      Soal Nomor {currentQuestionIndex + 1}
                    </CardTitle>
                    <span className={`badge p-2 font-size-12 bg-${
                      currentQuestion.jenis_soal === 'TWK' ? 'primary' :
                      currentQuestion.jenis_soal === 'TIU' ? 'info' :   // Using 'info' for TIU (or 'purple' if you define custom CSS)
                      currentQuestion.jenis_soal === 'TKP' ? 'success' : // Assign a color for TKP
                      'secondary' // Fallback for any other jenis_soal not explicitly listed
                  }`}>
                    {currentQuestion.jenis_soal || 'Jenis Soal'}
                  </span>
                  </div>

                  {currentQuestion.image_soal && (
                    <div className="mb-3 text-center p-2 rounded bg-light">
                      <img
                        src={currentQuestion.image_soal}
                        alt="Gambar Soal"
                        className="img-fluid rounded"
                        style={{ maxWidth: '100%', maxHeight: '250px', objectFit: 'contain' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <small className="text-muted mt-1 d-block">Gambar Soal</small>
                    </div>
                  )}
                  <div className="font-size-15 mb-3 lh-base" dangerouslySetInnerHTML={{ __html: currentQuestion.soal }} />
                  <div className="options-container mb-3">
                    {questionOptions.map((option) => {
                      const isSelected = userAnswers[currentQuestion.id_soal]?.answer === option.key;
                      return (
                        <div
                          key={option.key}
                          className={`d-flex align-items-start py-2 px-3 mb-2 border rounded ${isSelected ? 'border-primary bg-primary-10' : ''}`}
                          onClick={() => handleAnswerChange(currentQuestion.id_soal, option.key)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className={`flex-shrink-0 d-flex align-items-center justify-content-center me-2 mt-1 ${isSelected ? 'border-primary bg-primary' : 'border-secondary'}`}
                            style={{
                              width: '18px',
                              height: '18px',
                              borderRadius: '50%',
                              border: '2px solid'
                            }}
                          >
                            {isSelected && <div className="bg-white rounded-circle" style={{ width: '8px', height: '8px' }} />}
                          </div>

                          <Label className="flex-grow-1" style={{ cursor: 'pointer' }}>
                            <span className="fw-bold me-1">{option.char}.</span>
                            <div dangerouslySetInnerHTML={{ __html: option.text }} />
                            {option.image && (
                              <img
                                src={option.image}
                                alt={`Opsi ${option.key}`}
                                className="img-fluid ms-2 rounded mt-1"
                                style={{ maxHeight: '80px', objectFit: 'contain' }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            )}
                          </Label>
                        </div>
                      );
                    })}
                  </div>

                  <div className="d-flex justify-content-between mt-3 pt-2 border-top">
                    <Button
                      color="secondary"
                      outline
                      onClick={handlePreviousQuestion}
                      disabled={currentQuestionIndex === 0}
                      className="btn-md px-3"
                    >
                      <i className="bx bx-chevron-left me-1"></i> Sebelumnya
                    </Button>
                    {currentQuestionIndex < totalQuestions - 1 ? (
                      <Button
                        color="primary"
                        onClick={handleNextQuestion}
                        className="btn-md px-3"
                      >
                        Selanjutnya <i className="bx bx-chevron-right ms-1"></i>
                      </Button>
                    ) : (
                      <Button
                        color="success"
                        onClick={toggleConfirmModal}
                        disabled={isSubmitting}
                        className="btn-md px-3"
                      >
                        {isSubmitting ? <Spinner size="sm" /> : 'Selesai'} <i className="bx bx-check ms-1"></i>
                      </Button>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>

            {/* Kolom Navigasi */}
            <Col lg={3}>
              <div className="d-flex flex-column g-3">
                <Card className="border-0 shadow-sm">
                  <CardBody className="p-3">
                    <div className="text-center mb-3">
                      <div className="d-inline-flex align-items-center justify-content-center px-3 py-2 rounded-pill bg-danger-10 border border-danger">
                        <i className="bx bx-time-five font-size-18 me-2 text-danger"></i>
                        <span className="font-size-15 fw-bold text-danger">
                          Sisa Waktu: {formatTime(timeLeft)}
                        </span>
                      </div>
                    </div>

                    <div className="mb-3">
                      <h6 className="font-size-14 mb-2">Progres Ujian</h6>
                      <Progress value={progressPercentage} color="primary" className="mb-2" style={{ height: '8px' }} />
                      <p className="text-muted font-size-13 mb-0">{answeredQuestionsCount} dari {totalQuestions} soal terjawab</p>
                    </div>

                    <hr />

                    <h6 className="font-size-14 mb-4">Navigasi Soal</h6>
                    <div className="d-flex flex-wrap gap-2" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {examData.daftar_soal.map((_, index) => {
                        const isAnswered = userAnswers[examData.daftar_soal[index].id_soal]?.answer !== null;
                        const isCurrent = index === currentQuestionIndex;
                        
                        return (
                          <Button
                            key={index}
                            onClick={() => handleQuestionNavigation(index)}
                            className="btn-sm"
                            color={isCurrent ? 'primary' : isAnswered ? 'success' : 'light'}
                            style={{
                              width: 'calc((100% / 5) - (8px * 4 / 5))',
                              minWidth: '35px',
                              height: '40px',
                              borderRadius: '6px'
                            }}
                          >
                            {index + 1}
                          </Button>
                        );
                      })}
                    </div>
                     <Button
                        color="danger"
                        onClick={toggleConfirmModal}
                        disabled={isSubmitting}
                        className="w-100 mt-2 py-2"
                      >
                        {isSubmitting ? <Spinner size="sm" /> : 'Selesaikan Ujian'}
                    </Button>
                  </CardBody>
                </Card>
                
               
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Modal Konfirmasi Selesai Ujian */}
      <Modal isOpen={isConfirmModalOpen} toggle={toggleConfirmModal} centered>
        <ModalHeader toggle={toggleConfirmModal}>Konfirmasi Selesaikan Ujian</ModalHeader>
        <ModalBody>
          Apakah Anda yakin ingin menyelesaikan ujian? Anda tidak dapat kembali setelah ini.
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleConfirmModal}>Batal</Button>
          <Button color="success" onClick={handleFinishExam} disabled={isSubmitting}>
            {isSubmitting ? <Spinner size="sm" /> : 'Ya, Selesaikan'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal Blocker Navigasi */}
      <Modal isOpen={isBlockerModalOpen} toggle={cancelBlockerNavigation} centered backdrop="static" keyboard={false}>
        <ModalHeader>Perhatian!</ModalHeader>
        <ModalBody>
          Anda sedang dalam proses ujian. Jika Anda meninggalkan halaman ini, ujian akan otomatis diselesaikan dan jawaban Anda akan disimpan.
          Apakah Anda yakin ingin meninggalkan halaman ini?
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={cancelBlockerNavigation}>Batal</Button>
          <Button color="danger" onClick={confirmBlockerNavigation}>Ya, Tinggalkan & Selesaikan Ujian</Button>
        </ModalFooter>
      </Modal>

      <ToastContainer position="top-right" autoClose={5000} />
    </React.Fragment>
  );
};

export default TryoutExam;